import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/api';
import { Loader2, Plus, FolderPlus, Tag, Edit2, Trash2, ChevronRight, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function CategoryManager() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [isEditing, setIsLogin] = useState(null); // Reusing state for simplicity: editing category object
  const [formData, setFormData] = useState({
    name: '',
    label_en: '',
    label_he: '',
    parent: null
  });

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  // Organize categories into tree structure
  const mainCategories = categories.filter(c => !c.parent);
  const subCategories = (parentId) => categories.filter(c => c.parent === parentId);

  const createMutation = useMutation({
    mutationFn: (data) => createCategory(data),
    onSuccess: () => {
      toast.success(t('categoryCreated'));
      resetForm();
      queryClient.invalidateQueries(['categories']);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateCategory(id, data),
    onSuccess: () => {
      toast.success(t('statusUpdated'));
      resetForm();
      queryClient.invalidateQueries(['categories']);
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: () => {
      toast.success('Category removed');
      queryClient.invalidateQueries(['categories']);
    },
    onError: (error) => toast.error(error.message),
  });

  const resetForm = () => {
    setFormData({ name: '', label_en: '', label_he: '', parent: null });
    setIsLogin(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.label_en || !formData.label_he) {
      toast.error(t('fillAllFields'));
      return;
    }

    if (isEditing) {
      updateMutation.mutate({ id: isEditing._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (cat) => {
    setIsLogin(cat);
    setFormData({
      name: cat.name,
      label_en: cat.label_en,
      label_he: cat.label_he,
      parent: cat.parent || null
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this category? Subcategories will remain but lose their parent.')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return (
      <div className="flex justify-center p-12">
          <Loader2 className="animate-spin w-8 h-8 text-primary" />
      </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Form Section */}
      <div className="lg:col-span-1">
        <div className="bg-card rounded-3xl p-6 border border-border shadow-xl sticky top-24">
            <h2 className="text-xl font-black mb-6 flex items-center gap-3">
            {isEditing ? <Edit2 className="text-primary" /> : <FolderPlus className="text-primary" />}
            {isEditing ? 'Edit Category' : t('addCategory')}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Technical Name (Slug)</label>
                <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g. electronics"
                className="w-full bg-secondary/30 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-transparent"
                disabled={!!isEditing}
                />
            </div>
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">English Label</label>
                <input
                type="text"
                value={formData.label_en}
                onChange={(e) => setFormData({...formData, label_en: e.target.value})}
                placeholder="e.g. Electronics"
                className="w-full bg-secondary/30 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-transparent"
                />
            </div>
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Hebrew Label</label>
                <input
                type="text"
                value={formData.label_he}
                onChange={(e) => setFormData({...formData, label_he: e.target.value})}
                placeholder="לדוגמה: אלקטרוניקה"
                className="w-full bg-secondary/30 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-transparent"
                />
            </div>
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Parent Category (Optional)</label>
                <select
                    value={formData.parent || ''}
                    onChange={(e) => setFormData({...formData, parent: e.target.value || null})}
                    className="w-full bg-secondary/30 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-transparent appearance-none"
                >
                    <option value="">None (Main Category)</option>
                    {mainCategories.map(c => (
                        <option key={c._id} value={c._id}>{c.label_en} / {c.label_he}</option>
                    ))}
                </select>
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="flex-1 bg-primary text-primary-content font-black py-3 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 text-sm"
                >
                    {createMutation.isLoading || updateMutation.isLoading ? <Loader2 className="animate-spin size-4" /> : <Check size={18} />}
                    {isEditing ? 'Update' : t('addCategory')}
                </button>
                {isEditing && (
                    <button
                        type="button"
                        onClick={resetForm}
                        className="p-3 bg-muted rounded-xl hover:bg-muted-foreground/10 transition-colors"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>
            </form>
        </div>
      </div>

      {/* List Section */}
      <div className="lg:col-span-2 space-y-4">
        <h2 className="text-xl font-black flex items-center gap-3">
          <Tag className="text-primary" />
          {t('categories')}
        </h2>
        
        <div className="space-y-4">
          {mainCategories.map((cat) => (
            <div key={cat._id} className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="p-4 flex items-center justify-between bg-muted/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <Tag size={18} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">{cat.label_he} / {cat.label_en}</h3>
                    <span className="text-[10px] uppercase font-black tracking-widest opacity-50">{cat.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEdit(cat)} className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"><Edit2 size={16}/></button>
                  <button onClick={() => handleDelete(cat._id)} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors"><Trash2 size={16}/></button>
                </div>
              </div>

              {/* Subcategories */}
              <div className="p-4 pt-0 space-y-2">
                {subCategories(cat._id).map(sub => (
                  <div key={sub._id} className="ml-6 pl-4 border-l-2 border-border flex items-center justify-between py-2 group">
                    <div className="flex items-center gap-2">
                        <ChevronRight size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium">{sub.label_he} / {sub.label_en}</span>
                        <span className="text-[9px] opacity-40 uppercase font-bold">({sub.name})</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(sub)} className="p-1.5 hover:bg-primary/10 text-primary rounded-md transition-colors"><Edit2 size={12}/></button>
                        <button onClick={() => handleDelete(sub._id)} className="p-1.5 hover:bg-red-500/10 text-red-500 rounded-md transition-colors"><Trash2 size={12}/></button>
                    </div>
                  </div>
                ))}
                {subCategories(cat._id).length === 0 && (
                    <p className="text-[10px] text-muted-foreground italic ml-6 py-2">No subcategories</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
